import React from 'react';
import './DetailsSection.css';
import CalendarDayIcon from '../calendar/CalendarDayIcon';

export default function EventDetailsSection({
  event,
  eventInfo,
  isPastEvent,
  endeavor,
  onEdit,
  onGoToEndeavor
}) {
  if (!event) return null;

  const eventDate = event.startDatetime || event.eventDate || new Date();

  // Read course location from endeavor metadata (fallback to endeavor.location)
  const endeavorLocation = (() => {
    const meta = Array.isArray(endeavor?.metadata) ? endeavor.metadata : [];
    const item = meta.find(m => (m?.metadataName || '').trim().toLowerCase() === 'course location');
    const val = (item?.value || '').toString().trim();
    return val || endeavor?.location || null;
  })();

  const typeEmoji = (() => {
    const t = (event.eventType || '').toLowerCase();
    if (t.includes('lecture') || t.includes('class')) return '🎓';
    if (t.includes('lab')) return '🔬';
    if (t.includes('exam')) return '📝';
    if (t.includes('quiz')) return '❓';
    if (t.includes('discussion')) return '💬';
    return null;
  })();

  const isCanceled = Boolean(event.canceled);
  const isFromSyllabus = Boolean(event.isFromSyllabus); // added
  // New unified status token key
  const statusKey = isCanceled ? 'canceled' : (isPastEvent ? 'past' : 'upcoming');

  return (
    <div
      className={`event-combined ${isPastEvent ? 'past-event' : ''}`}
      data-status={statusKey}
      data-canceled={isCanceled || undefined}
      data-surface="event-details"
    >
      <div className="event-top-row">
        <div className="event-title-section">
          <div className="event-icon-large event-icon-with-calendar">
            <CalendarDayIcon date={eventDate} size={64} />
            {typeEmoji && <span className="event-type-overlay" title={event.eventType}>{typeEmoji}</span>}
          </div>
          <div className="event-title-meta">
            <h1 className="event-title">{event.name || event.title}</h1>
            <div className="event-meta">
              <span className="event-type chip" data-chip="type">{eventInfo.typeName}</span>
              {typeof event.sequence === 'number' && (
                <span className="event-type chip" data-chip="session" title="Session number">Session #{event.sequence}</span>
              )}
              {isCanceled && <span className="event-type chip" data-chip="canceled" title="This session is canceled">❌ Canceled</span>}
              {isFromSyllabus && <span className="event-type chip" data-chip="syllabus" title="Extracted from syllabus">🤖 From Syllabus</span>}
              {endeavor && (
                <span
                  className="event-endeavor chip clickable"
                  data-chip="course"
                  onClick={onGoToEndeavor}
                >
                  {endeavor.name}
                </span>
              )}
              {event.location && <span className="location-badge chip" data-chip="location">📍 {event.location}</span>}
              {endeavorLocation && endeavorLocation !== event.location && (
                <span className="location-badge chip" data-chip="course-location" title="Course location">🏫 {endeavorLocation}</span>
              )}
            </div>
          </div>
        </div>
        <div className="combined-actions">
          <button
            className="btn btn-primary edit-event-btn mobile-hide"
            onClick={onEdit}
            disabled={!event.eventId && !event.id}
          >✏️ Edit Event</button>
        </div>
      </div>

      {event.description && (
        <div className="event-description-block">{event.description}</div>
      )}

      <div className="event-details-grid">
        <div className="detail-item">
          <button className="detail-item-edit-btn" onClick={onEdit} title="Edit start time">✏️</button>
          <label>Start</label>
          <p className={isPastEvent ? 'past-text' : ''}>{eventInfo.start}</p>
        </div>
        <div className="detail-item">
          <button className="detail-item-edit-btn" onClick={onEdit} title="Edit end time">✏️</button>
          <label>End</label>
          <p className={isPastEvent ? 'past-text' : ''}>{eventInfo.end}</p>
        </div>
        <div className="detail-item">
          <button className="detail-item-edit-btn" onClick={onEdit} title="Edit duration">✏️</button>
          <label>Duration</label>
          <p>{eventInfo.duration}</p>
        </div>
        <div className="detail-item">
          <button className="detail-item-edit-btn" onClick={onEdit} title="Edit status">✏️</button>
          <label>Status</label>
          <p>
            <span
              className="status-badge"
              data-status={statusKey}
            >
              {isCanceled ? 'Canceled' : eventInfo.status}
            </span>
          </p>
        </div>
        {typeof event.sequence === 'number' && (
          <div className="detail-item">
            <button className="detail-item-edit-btn" onClick={onEdit} title="Edit session">✏️</button>
            <label>Session</label>
            <p>#{event.sequence}</p>
          </div>
        )}
        {event.location && (
          <div className="detail-item">
            <button className="detail-item-edit-btn" onClick={onEdit} title="Edit location">✏️</button>
            <label>Location</label>
            <p>📍 {event.location}</p>
          </div>
        )}
        {endeavorLocation && endeavorLocation !== event.location && (
          <div className="detail-item">
            <button className="detail-item-edit-btn" onClick={onEdit} title="Edit course location">✏️</button>
            <label>Course Location</label>
            <p>🏫 {endeavorLocation}</p>
          </div>
        )}
        <div className="detail-item">
          <button className="detail-item-edit-btn" onClick={onEdit} title="Edit event">✏️</button>
          <label>Created</label>
          <p>{eventInfo.createdAt}</p>
        </div>
        <div className="detail-item">
          <button className="detail-item-edit-btn" onClick={onEdit} title="Edit event">✏️</button>
          <label>Updated</label>
          <p>{eventInfo.updatedAt}</p>
        </div>

        {(Array.isArray(event.topics) && event.topics.length > 0) && (
          <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
            <button className="detail-item-edit-btn" onClick={onEdit} title="Edit topics">✏️</button>
            <label>Topics</label>
            <p>{event.topics.join(' • ')}</p>
          </div>
        )}
        {(Array.isArray(event.readings) && event.readings.length > 0) && (
          <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
            <button className="detail-item-edit-btn" onClick={onEdit} title="Edit readings">✏️</button>
            <label>Readings</label>
            <p>{event.readings.join(' • ')}</p>
          </div>
        )}
      </div>
    </div>
  );
}